import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { ArrowLeft, X, Plus, Check, MapPin, Home, IndianRupee, Ruler, Building } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PropertyCompare() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSelector, setShowSelector] = useState(false);

  useEffect(() => {
    fetchProperties();
    // Load pre-selected properties from URL params
    const ids = searchParams.get("ids");
    if (ids) {
      const propertyIds = ids.split(",");
      setSelectedProperties(propertyIds);
    }
  }, [searchParams]);

  const fetchProperties = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/properties`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProperties(response.data);
    } catch (error) {
      toast.error("Failed to load properties");
    } finally {
      setLoading(false);
    }
  };

  const toggleProperty = (propertyId) => {
    if (selectedProperties.includes(propertyId)) {
      setSelectedProperties(prev => prev.filter(id => id !== propertyId));
    } else if (selectedProperties.length < 3) {
      setSelectedProperties(prev => [...prev, propertyId]);
    } else {
      toast.error("You can compare up to 3 properties only");
    }
  };

  const getSelectedPropertyData = () => {
    return selectedProperties.map(id => properties.find(p => p.id === id)).filter(Boolean);
  };

  const formatPrice = (price) => {
    if (price >= 10000000) {
      return `₹${(price / 10000000).toFixed(2)} Cr`;
    } else if (price >= 100000) {
      return `₹${(price / 100000).toFixed(2)} L`;
    }
    return `₹${price.toLocaleString('en-IN')}`;
  };

  const comparisonData = getSelectedPropertyData();

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto fade-in">
        <Button
          data-testid="back-button"
          onClick={() => navigate("/properties")}
          variant="ghost"
          className="mb-6 -ml-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Properties
        </Button>

        <div className="bg-white rounded-md border border-slate-200 shadow-sm">
          <div className="p-6 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                Compare Properties
              </h1>
              <p className="text-slate-500 mt-1">Select up to 3 properties to compare side-by-side</p>
            </div>
            <Button
              onClick={() => setShowSelector(!showSelector)}
              className="bg-blue-900 hover:bg-blue-800 text-white"
              data-testid="add-property-to-compare"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Property ({selectedProperties.length}/3)
            </Button>
          </div>

          {/* Property Selector */}
          {showSelector && (
            <div className="p-6 bg-slate-50 border-b border-slate-200">
              <h3 className="font-medium text-slate-700 mb-4">Select Properties to Compare</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                {properties.map(property => (
                  <div
                    key={property.id}
                    onClick={() => toggleProperty(property.id)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedProperties.includes(property.id)
                        ? 'border-amber-500 bg-blue-50'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-slate-900 line-clamp-1">{property.title}</h4>
                        <p className="text-sm text-slate-500 flex items-center mt-1">
                          <MapPin className="h-3 w-3 mr-1" />
                          {property.location}
                        </p>
                        <p className="text-blue-700 font-semibold mt-2">{formatPrice(property.price)}</p>
                      </div>
                      {selectedProperties.includes(property.id) && (
                        <div className="bg-amber-500 rounded-full p-1">
                          <Check className="h-4 w-4 text-white" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <Button
                onClick={() => setShowSelector(false)}
                className="mt-4"
                variant="outline"
              >
                Done
              </Button>
            </div>
          )}

          {/* Comparison Table */}
          {comparisonData.length === 0 ? (
            <div className="p-12 text-center">
              <Building className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">No properties selected</h3>
              <p className="text-slate-500 mt-1">Click "Add Property" to start comparing</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="p-4 text-left text-sm font-medium text-slate-500 w-48">Feature</th>
                    {comparisonData.map(property => (
                      <th key={property.id} className="p-4 text-left min-w-64">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-slate-900 line-clamp-2">{property.title}</h3>
                            <p className="text-sm text-slate-500 flex items-center mt-1">
                              <MapPin className="h-3 w-3 mr-1" />
                              {property.location}
                            </p>
                          </div>
                          <button
                            onClick={() => toggleProperty(property.id)}
                            className="text-slate-400 hover:text-[#0f2744] p-1"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Price */}
                  <tr className="border-b border-slate-100">
                    <td className="p-4 text-sm font-medium text-slate-700 bg-slate-50">
                      <IndianRupee className="h-4 w-4 inline mr-2" />
                      Price
                    </td>
                    {comparisonData.map(property => (
                      <td key={property.id} className="p-4">
                        <span className="text-lg font-bold text-blue-700">{formatPrice(property.price)}</span>
                        {property.rate_amount && (
                          <span className="block text-sm text-slate-500">
                            + {formatPrice(property.rate_amount)}/{property.rate || 'month'}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>

                  {/* Property Type & Purpose */}
                  <tr className="border-b border-slate-100">
                    <td className="p-4 text-sm font-medium text-slate-700 bg-slate-50">
                      <Home className="h-4 w-4 inline mr-2" />
                      Type & Purpose
                    </td>
                    {comparisonData.map(property => (
                      <td key={property.id} className="p-4">
                        <span className="capitalize">{property.property_type}</span>
                        <span className="mx-2 text-slate-300">•</span>
                        <span className="capitalize text-slate-600">{property.purpose}</span>
                      </td>
                    ))}
                  </tr>

                  {/* Area */}
                  <tr className="border-b border-slate-100">
                    <td className="p-4 text-sm font-medium text-slate-700 bg-slate-50">
                      <Ruler className="h-4 w-4 inline mr-2" />
                      Area
                    </td>
                    {comparisonData.map(property => (
                      <td key={property.id} className="p-4">
                        <div><strong>Carpet:</strong> {property.area} sq ft</div>
                        {property.built_up_area && <div className="text-sm text-slate-500">Built-up: {property.built_up_area} sq ft</div>}
                        {property.super_built_up_area && <div className="text-sm text-slate-500">Super Built-up: {property.super_built_up_area} sq ft</div>}
                      </td>
                    ))}
                  </tr>

                  {/* Configuration */}
                  <tr className="border-b border-slate-100">
                    <td className="p-4 text-sm font-medium text-slate-700 bg-slate-50">Configuration</td>
                    {comparisonData.map(property => (
                      <td key={property.id} className="p-4">
                        <div className="flex flex-wrap gap-2">
                          {property.bedrooms && <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">{property.bedrooms} BHK</span>}
                          {property.bathrooms && <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-sm">{property.bathrooms} Bath</span>}
                          {property.balconies && <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm">{property.balconies} Balcony</span>}
                          {property.parking && <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-sm">{property.parking} Parking</span>}
                        </div>
                      </td>
                    ))}
                  </tr>

                  {/* Furnishing */}
                  <tr className="border-b border-slate-100">
                    <td className="p-4 text-sm font-medium text-slate-700 bg-slate-50">Furnishing</td>
                    {comparisonData.map(property => (
                      <td key={property.id} className="p-4">
                        <span className="capitalize">{property.furnishing || 'Not specified'}</span>
                        {property.furnishing_details && (
                          <p className="text-sm text-slate-500 mt-1">{property.furnishing_details}</p>
                        )}
                      </td>
                    ))}
                  </tr>

                  {/* Floor & Facing */}
                  <tr className="border-b border-slate-100">
                    <td className="p-4 text-sm font-medium text-slate-700 bg-slate-50">Floor & Facing</td>
                    {comparisonData.map(property => (
                      <td key={property.id} className="p-4">
                        {property.floor_number && (
                          <div>Floor {property.floor_number}{property.total_floors ? ` of ${property.total_floors}` : ''}</div>
                        )}
                        {property.facing && <div className="text-slate-600 capitalize">{property.facing} facing</div>}
                      </td>
                    ))}
                  </tr>

                  {/* Age & Possession */}
                  <tr className="border-b border-slate-100">
                    <td className="p-4 text-sm font-medium text-slate-700 bg-slate-50">Age & Possession</td>
                    {comparisonData.map(property => (
                      <td key={property.id} className="p-4">
                        {property.age_of_property && <div className="capitalize">{property.age_of_property.replace(/_/g, ' ')}</div>}
                        {property.possession_status && (
                          <div className="text-slate-600 capitalize">{property.possession_status.replace(/_/g, ' ')}</div>
                        )}
                      </td>
                    ))}
                  </tr>

                  {/* Room Measurements */}
                  <tr className="border-b border-slate-100">
                    <td className="p-4 text-sm font-medium text-slate-700 bg-slate-50">Room Sizes</td>
                    {comparisonData.map(property => (
                      <td key={property.id} className="p-4">
                        {property.room_measurements && property.room_measurements.length > 0 ? (
                          <div className="space-y-1">
                            {property.room_measurements.slice(0, 5).map((room, idx) => (
                              <div key={idx} className="text-sm">
                                <span className="font-medium">{room.room_type}:</span> {room.area || `${room.length}×${room.width}`} {room.unit === 'sq_m' ? 'sq m' : 'sq ft'}
                              </div>
                            ))}
                            {property.room_measurements.length > 5 && (
                              <div className="text-xs text-slate-500">+{property.room_measurements.length - 5} more rooms</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400">Not available</span>
                        )}
                      </td>
                    ))}
                  </tr>

                  {/* Plot Dimensions */}
                  <tr className="border-b border-slate-100">
                    <td className="p-4 text-sm font-medium text-slate-700 bg-slate-50">Plot Size</td>
                    {comparisonData.map(property => (
                      <td key={property.id} className="p-4">
                        {property.plot_dimensions && (property.plot_dimensions.area || property.plot_dimensions.length) ? (
                          <div>
                            {property.plot_dimensions.area && <div>{property.plot_dimensions.area} {property.plot_dimensions.unit === 'sq_m' ? 'sq m' : 'sq ft'}</div>}
                            {property.plot_dimensions.length && property.plot_dimensions.width && (
                              <div className="text-sm text-slate-500">
                                {property.plot_dimensions.length} × {property.plot_dimensions.width}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400">Not available</span>
                        )}
                      </td>
                    ))}
                  </tr>

                  {/* Amenities */}
                  <tr className="border-b border-slate-100">
                    <td className="p-4 text-sm font-medium text-slate-700 bg-slate-50">Amenities</td>
                    {comparisonData.map(property => (
                      <td key={property.id} className="p-4">
                        {property.amenities && property.amenities.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {property.amenities.slice(0, 6).map((amenity, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                                {amenity}
                              </span>
                            ))}
                            {property.amenities.length > 6 && (
                              <span className="px-2 py-0.5 bg-slate-200 text-slate-600 rounded text-xs">
                                +{property.amenities.length - 6} more
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400">None listed</span>
                        )}
                      </td>
                    ))}
                  </tr>

                  {/* Price per sq ft */}
                  <tr className="border-b border-slate-100">
                    <td className="p-4 text-sm font-medium text-slate-700 bg-slate-50">Price/sq ft</td>
                    {comparisonData.map(property => (
                      <td key={property.id} className="p-4">
                        <span className="font-semibold text-blue-700">
                          ₹{Math.round(property.price / parseInt(property.area)).toLocaleString('en-IN')}/sq ft
                        </span>
                      </td>
                    ))}
                  </tr>

                  {/* Actions */}
                  <tr>
                    <td className="p-4 text-sm font-medium text-slate-700 bg-slate-50">Actions</td>
                    {comparisonData.map(property => (
                      <td key={property.id} className="p-4">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/properties/edit/${property.id}`)}
                          >
                            View Details
                          </Button>
                        </div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
